import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { CommonFacilityModel } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { PriceModel, PriceParams, ProductModel } from "src/api/product/product-types";
import { getPriceAsync, getReservationPoliciesAsync, getReservedTimesAsync, getReserveDurationAsyc } from "src/api/reservation/reservation-api";
import {
  AcknowledgeReservation,
  AvailableReservations,
  ClosedReason,
  ClosingRecurrence,
  ReservationPolicyModel,
  ScheduleData,
} from "src/api/reservation/reservation-type";
import { getSalePeriodMessage, ViewYmdFormat, YmdFormat } from "src/utils/common-util";
import { findClosedRangeOfSlot, getClosedRangesOfDate, getClosingRecurrences } from "./closing-policy";

const compareDateTime = (date: string) => {
  return Math.floor(new Date(date).getTime() / 1000);
};

// 상품 판매기간 밖인지 확인 > 오픈 예정 / 판매 마감 상태면 전체 예약 불가
const isOutOfSalePeriod = (product?: ProductModel) => {
  if (!product) return false;

  const { isAvailableSaleStartDate, isAvailableSaleEndDate } = getSalePeriodMessage(product);
  return !(isAvailableSaleStartDate && isAvailableSaleEndDate);
};

// 이미 지나간 slot 인지 확인 > 종료시간이 현재 시각을 지났으면 과거
const isPastSlot = (item: ScheduleData) => moment(item.endDate).isSameOrBefore(moment());

// 점유된 예약 구간에 완전히 포함되는 slot 인지 확인
const isReservedSlot = (item: ScheduleData, reserved: AcknowledgeReservation[]) => {
  const startDate = compareDateTime(item.startDate);
  const endDate = compareDateTime(item.endDate);

  return reserved.some((reservationItem) => startDate >= compareDateTime(reservationItem.start) && endDate <= compareDateTime(reservationItem.end));
};

// 예약 가능 기간(현재 시각 기준 상대 구간) 밖의 slot 인지 확인
const isOutOfAvailableReservationPeriod = (item: ScheduleData, reserveDuration?: AvailableReservations) => {
  if (!reserveDuration?.id) return false;

  const availableStartTime = Number(reserveDuration.availableReservationTimeStart);
  const availableEndTime = Number(reserveDuration.availableReservationTimeEnd);

  // 시작/종료 모두 미설정이거나 제한을 쓰지 않으면 전체 예약 가능
  if ((availableStartTime < 0 && availableEndTime < 0) || !reserveDuration.isLimitedAvailableReservationTime) return false;

  const currentTime = moment();
  let isBlockSlot = false;

  //현재시간 + 최소시간 이전에 시작하는 slot 차단
  if (availableStartTime > 0) {
    isBlockSlot = moment(item.startDate).isSameOrBefore(currentTime.clone().add(availableStartTime, "minutes"));
  }
  //현재시간 + 최대시간 이후에 끝나는 slot 차단
  if (availableEndTime > 0) {
    if (currentTime.clone().add(availableEndTime, "minutes").isSameOrBefore(item.endDate)) isBlockSlot = true;
  }

  return isBlockSlot;
};

type Props = {
  product?: ProductModel;
  facility?: CommonFacilityModel;
};

//Api 호출영역 커스텀 훅
const useReservationApi = () => {
  //Api 호출
  const { executeAsync: getPrices } = useApiOperation(getPriceAsync, { noAuthenticationRequired: true }); // 요금조회 api
  const { executeAsync: getReservationPolicies } = useApiOperation(getReservationPoliciesAsync, { noAuthenticationRequired: true }); // 유효 예약 정책 조회 api
  const { executeAsync: getReservedTimes } = useApiOperation(getReservedTimesAsync, { noAuthenticationRequired: true }); // 스케쥴 예약 점유확인 api
  const { executeAsync: getReserveDuration } = useApiOperation(getReserveDurationAsyc, { noAuthenticationRequired: true }); // 스케쥴 예약 가능한 구간 api

  // 요금 가져오기
  const fetchPrices = async (product: ProductModel, facility: CommonFacilityModel, selectedDate: string) => {
    // setCalendarDate(selectedDate);

    const priceData: PriceParams = {
      providerId: String(product?.providerId),
      serviceId: String(product?.id),
      serviceType: "SERVICE_PRODUCT",
      startDate: moment(selectedDate).startOf("day").format(YmdFormat.WITH_TIME_ZONE),
      endDate: moment(selectedDate).add(1, "days").startOf("day").format(YmdFormat.WITH_TIME_ZONE),
      buildingCommonFacilityId: String(facility?.id),
    };

    try {
      const { data } = await getPrices(priceData);
      return data.data.content;
    } catch (error) {
      console.error("Error fetching prices:", error);
      return [];
    }
  };

  // 점유 목록
  const fetchReservedTime = async (facility: CommonFacilityModel, selectedDate: string) => {
    try {
      const { data } = await getReservedTimes({
        facilityId: facility?.id,
        start: moment(selectedDate).startOf("day").format(YmdFormat.WITH_TIME_ZONE),
        end: moment(selectedDate).add(1, "days").startOf("day").format(YmdFormat.WITH_TIME_ZONE),
      });
      return data.data.content;
    } catch (error) {
      console.error("Error fetching reserved:", error);
      return [];
    }
  };

  // 공용공간에 적용된 유효 예약 정책 [예약 시작 시간 설정, 휴무일, 이용 한도]
  const fetchReservationPolicies = async (product: ProductModel, facility: CommonFacilityModel) => {
    try {
      const { data } = await getReservationPolicies({ productId: String(product.id), facilityIds: [String(facility.id)] });
      // 응답 순서는 요청 순서와 무관하므로 facilityId 로 매칭한다
      return data?.facilities?.find((item) => String(item.facilityId) === String(facility.id))?.policies || [];
    } catch (error) {
      console.error("Error fetching ReservationPolicies:", error);
      return [];
    }
  };

  // 점유 목록
  const fetchReserveDuration = async (product: ProductModel, facility: CommonFacilityModel) => {
    const { data, status } = await getReserveDuration({
      serviceId: String(product?.id),
      serviceType: "SERVICE_PRODUCT",
      commonFacilityType: facility?.commonFacilityType || "COMMONFACILITY_UNRECOGNIZED",
      buildingCommonFacilityId: String(facility?.id),
    });

    if (status >= 200 && status <= 299) {
      return data.data.content[0];
    }
  };

  return { fetchPrices, fetchReservedTime, fetchReservationPolicies, fetchReserveDuration };
};

// 화면에 노출하는 스케줄 슬롯 영역 커스텀훅
const useSchedule = ({ product, facility }: Props) => {
  //state
  const [prices, setPrices] = useState<PriceModel[]>([]); // 요금 목록
  const [reserved, setReserved] = useState<AcknowledgeReservation[]>([]); // 점유 예약내역
  const [reserveDuration, setReserveDuration] = useState<AvailableReservations>(); //예약 가능 구간 > 현재 시간으로부터 ~부터 클릭 가능 && 최대 몇시간 선태 가능
  const [policies, setPolicies] = useState<ReservationPolicyModel[]>([]); //공용공간에 적용된 유효 예약 정책 목록
  const [calendardate, setCalendarDate] = useState<string>(moment().format(YmdFormat.WITH_TIME_ZONE)); //캘린더에 선택된 날짜 > default:오늘날짜
  const [scheduleSlots, setScheduleSlots] = useState<ScheduleData[]>([]); // 화면에 보여지는 요금 slots 데이터
  const [updatedAt, setUpdatedAt] = useState<string>(""); // 스케줄 데이터를 마지막으로 조회한 시각

  const { fetchPrices, fetchReservedTime, fetchReservationPolicies, fetchReserveDuration } = useReservationApi();

  // 선택된 예약 정책의 휴무일 목록 > 일자와 무관한 반복 설정이라 slot 판정 시점에 일자로 전개한다
  const closingRecurrences: ClosingRecurrence[] = useMemo(() => getClosingRecurrences(policies), [policies]);

  // 마지막으로 시작한 조회의 식별자. 날짜를 빠르게 넘기면 응답이 요청 순서와 다르게 도착할 수 있다
  const latestRequestIdRef = useRef(0);

  // 선택된 날짜 기준 스케줄 관련 데이터 조회 [요금, 점유, 예약 정책, 예약가능 구간]
  const fetchScheduleData = async () => {
    if (!product?.id || !facility) return;

    const requestId = ++latestRequestIdRef.current;

    const [priceData, reservedData, policyData, reserveDurationData] = await Promise.all([
      fetchPrices(product, facility, calendardate),
      fetchReservedTime(facility, calendardate),
      fetchReservationPolicies(product, facility),
      fetchReserveDuration(product, facility),
    ]);

    // 이미 더 최신 조회가 시작됐으면, 늦게 도착한 이전 응답으로 화면을 덮어쓰지 않는다
    if (requestId !== latestRequestIdRef.current) return;

    setPrices(priceData);
    setReserved(reservedData);
    setPolicies(policyData);
    setReserveDuration(reserveDurationData);
    setUpdatedAt(moment().format(ViewYmdFormat.HH_MM));
  };

  // facility 는 product 와 함께 세팅되지만, deps 에서 빠지면 batching 동작에 결과가 좌우된다
  useEffect(() => {
    fetchScheduleData();
  }, [product?.id, facility?.id, calendardate]);

  //현재 시간 확인
  //예약구간 최소시간 확인
  //slots > start 또는 end time 비교해서 최소구간 전에 비활성화

  const generateScheduleSlots = () => {
    // 판매기간 밖이면 slot 전체가 예약 불가
    const outOfSalePeriod = isOutOfSalePeriod(product);

    // 선택 날짜에 적용되는 휴무 구간
    const closedRanges = getClosedRangesOfDate(calendardate, closingRecurrences);

    // 선택 불가 사유 우선순위 > 계약 기간 외 > 과거 경과 시간 > 예약 점유 > 휴무 기간 > 예약 가능 기간 외
    // 복수 위반 시 대표 사유(외 n개)를 뽑아야 하므로 해당하는 사유를 모두 우선순위 순으로 담는다
    const schedule = prices.map((item: ScheduleData) => {
      const closedReasons: ClosedReason[] = [];

      if (outOfSalePeriod) closedReasons.push("OUT_OF_SALE_PERIOD");

      const isPast = isPastSlot(item);
      if (isPast) closedReasons.push("PAST");

      if (isReservedSlot(item, reserved)) closedReasons.push("RESERVED");

      // 휴무 구간에 조금이라도 겹치는 slot 을 휴무로 보고, 안내 문구에 쓸 휴무 사유를 함께 담는다
      const closedRange = findClosedRangeOfSlot(item, closedRanges);
      if (closedRange) closedReasons.push("CLOSED_PERIOD");

      // 예약 가능 기간은 현재 시각 기준 상대 구간이라 과거 slot 은 항상 구간 밖이다.
      // 과거 경과 시간은 gray 없이 노출해야 하는데 사유가 하나 더 붙으면 gray 가 되므로 과거 slot 은 판단에서 제외한다
      if (!isPast && isOutOfAvailableReservationPeriod(item, reserveDuration)) closedReasons.push("OUT_OF_PERIOD");

      if (closedReasons.length === 0) return item;

      return { ...item, isClosed: true, closedReasons, closedTitle: closedRange?.title };
    });
    setScheduleSlots(schedule);
  };
  useEffect(() => {
    generateScheduleSlots();
  }, [prices, reserved, closingRecurrences, reserveDuration, calendardate]);

  return {
    scheduleSlots,
    calendardate,
    setCalendarDate,
    updatedAt,
    reserveDuration,
    policies,
    closingRecurrences,
    refetchSchedule: fetchScheduleData,
  };
};

export default useSchedule;
